import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { RetryIcon } from "@solomon-labs/design/components/icons";
import { Button } from "@solomon-labs/design/components/ui";
import React, { useId } from "react";

import { ReportsAssemblingIllustration } from "./ReportsAssemblingIllustration";

/**
 * The Reporting page body when there is no chart to show: before any chart
 * has enough history (`empty`), or when a reporting request failed
 * (`error`).
 *
 * One shell for both, laid out as the retail Stats pages'
 * `DistributionHistoryEmpty` / `DistributionHistoryError`: the illustration,
 * then an h3 heading, then one small neutral line under it. The two states
 * are told apart by what they say, not by layout; a failed fetch must never
 * read as an account with no history.
 */
const COPY = {
    empty: {
        title: "Reports Not Available Yet",
        body: "We’ll be adding your reports shortly."
    },
    error: {
        title: "Reports Failed to Load",
        body: "Your positions keep earning. This error doesn’t affect rewards."
    }
} as const;

export function ReportingState({
    kind,
    onRetry
}: {
    kind: "empty" | "error";
    /** Error only: re-runs the failed reporting requests. */
    onRetry?: () => void;
}): React.ReactElement {
    const headingId = useId();
    const copy = COPY[kind];
    return (
        <section
            aria-labelledby={headingId}
            data-reporting-state={kind}
            className="flex flex-col items-center px-4 py-10 text-center"
        >
            <ReportsAssemblingIllustration className="mx-auto mb-4 w-full max-w-3xl" />
            <div className="flex flex-col items-center">
                <Text
                    variant="h3"
                    render={<h3 id={headingId} />}
                    className="text-balance text-foreground-base"
                >
                    {copy.title}
                </Text>
                <Text
                    variant="small"
                    render={<p />}
                    className="mt-2 max-w-sm text-balance text-foreground-neutral"
                >
                    {copy.body}
                </Text>
                {kind === "error" && onRetry ? (
                    <Button
                        variant="link"
                        interaction="underline"
                        size="2xs"
                        trailing={<RetryIcon />}
                        onClick={onRetry}
                        className="mt-3"
                    >
                        Retry
                    </Button>
                ) : null}
            </div>
        </section>
    );
}
